/**
 * pill-header.js
 * ---------------------------------------------------------------------------------------------------------
 * Mega menu behaviour for the header. Three concerns, in dependency order:
 *
 *   1. State      — maintain data-menu-open on <html> as the single source of truth for "a mega menu is open"
 *   2. Swap       — moving between top-level items exchanges panels with no animation and no visible gap
 *   3. Hover-out  — the menu stays open until the pointer actually leaves the header
 *
 * Loaded in both header styles. Only the CSS that consumes data-menu-open is pill-specific; the swap and the
 * hover-out behaviour apply to any mega menu.
 *
 * Nothing here depends on load order relative to theme.js. That was true of an earlier version and it was a
 * hidden contract — see the hover-out section for what replaced it.
 * ---------------------------------------------------------------------------------------------------------
 */
(() => {
  if (window.__ossahPillHeader) return;
  window.__ossahPillHeader = true;

  const root = document.documentElement;

  const PANEL = 'header-mega-menu';
  const OPEN_PANEL = 'header-mega-menu[open]';
  const ACTIVATOR = '[aria-controls][data-open-on-hover]';
  const KEEP_OPEN = '.shopify-section--header';

  /**
   * How long the open flag survives after the last panel closes.
   *
   * This is the whole reason the flag exists. A swap closes one panel and opens another, and depending on
   * listener registration order there can be a moment with zero panels open. Any rule that derives its own
   * state from [open] flickers on that moment. Holding the flag briefly means the swap completes inside the
   * window and the flag never drops — so no rule downstream needs a compensating transition delay.
   *
   * Long enough to cover a swap, short enough to be invisible on a real close.
   */
  const CLOSE_DELAY = 120;

  const anyOpen = () => document.querySelector(OPEN_PANEL) !== null;

  /* ── 0. Canvas scrim ───────────────────────────────────────────────────────────────────────────────── */

  /**
   * The brightness filter in pill-header.css dims everything that renders page content, but not the canvas
   * showing through the header section — the pill's inline gutters and the gap above it. This element covers
   * that band. See the paint-order note in pill-header.css for where z-index:-1 puts it and why.
   *
   * Created here rather than authored into header.liquid so it lives outside the header section entirely:
   * Section Rendering can replace that section at will, and a scrim inside it would be destroyed and rebuilt
   * mid-interaction. Prepended so it precedes every section in DOM order.
   *
   * Gated on --pill-scrim-alpha, which the section emits only in pill mode. A full-bleed header keeps Snow's
   * own mega menu scrim and gets no element at all. Reading it at load is safe because the variable is a
   * static declaration in the page's own markup, not something set later by script.
   */
  const buildScrim = () => {
    if (document.querySelector('.page-scrim')) return;

    const alpha = getComputedStyle(root).getPropertyValue('--pill-scrim-alpha').trim();
    if (alpha === '' || parseFloat(alpha) === 0) return;

    const scrim = document.createElement('div');
    scrim.className = 'page-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    document.body.prepend(scrim);
  };


  /* ── 0b. Sticky add-to-cart flag ───────────────────────────────────────────────────────────────────── */

  /**
   * The bar sits flush against the bottom of the pill, so while it is up the pill has to give up its own
   * bottom corners — the same move it already makes when a mega menu opens, and for the same reason: two
   * rounded edges touching pinch at the seam.
   *
   * Snow toggles .is-visible on the bar from its own observer. Mirroring that onto <html> keeps the CSS
   * reading a flag on the root like every other open-state rule in this subsystem, rather than reaching
   * across the document with :has(). The outer <product-sticky-add-to-cart> element is stable — variant
   * changes re-render <product-rerender> inside it — so one observer holds for the life of the page.
   */
  const watchStickyAtc = () => {
    const bar = document.querySelector('product-sticky-add-to-cart');
    if (!bar) return;

    const sync = () => root.toggleAttribute('data-sticky-atc', bar.classList.contains('is-visible'));

    new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['class'] });
    sync();
  };

  const onReady = () => {
    buildScrim();
    watchStickyAtc();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  /* ── 1. State ──────────────────────────────────────────────────────────────────────────────────────── */

  let closeTimer = null;

  const syncState = () => {
    if (anyOpen()) {
      if (closeTimer !== null) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      root.setAttribute('data-menu-open', '');
      return;
    }

    if (closeTimer !== null) return;

    closeTimer = setTimeout(() => {
      closeTimer = null;
      if (!anyOpen()) root.removeAttribute('data-menu-open');
    }, CLOSE_DELAY);
  };

  /**
   * A MutationObserver rather than dialog:before-show / dialog:after-hide.
   *
   * The [open] attribute IS the state — the events are Snow's narration of it, they do not bubble, and their
   * names are an implementation detail that could change. Watching the attribute cannot drift from reality,
   * and it also covers panels opened by keyboard, by click, or by anything an app does.
   *
   * Observing documentElement rather than the header section survives Section Rendering replacing the header.
   * attributeFilter keeps the callback cheap, and because data-menu-open is not in that filter, writing it
   * cannot re-trigger the observer.
   */
  new MutationObserver(syncState).observe(root, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open']
  });

  syncState();

  /* ── 2. Swap ───────────────────────────────────────────────────────────────────────────────────────── */

  /**
   * HeaderMegaMenu.createEnterAnimationControls fades the panel from opacity 0 to 1 over 0.2s. Opening from
   * nothing, that reads well. Swapping, it does not: show() writes an inline display value, so the outgoing
   * panel stays painted through its 0.2s leave animation while the incoming one is still near transparent,
   * and the dimmed page shows through where the menu should be.
   *
   * DialogElement.show() already has a no-animation path — it calls animationControls.complete() when
   * animate is false. We cannot reach that argument from outside, but we can shadow the factory on the
   * instance and complete the controls it returns, which lands on exactly the same code path. The returned
   * object is still the real one, so dialog:after-show still fires and focus trapping still waits on it.
   *
   * This replaced a CSS class pinning opacity with !important plus a cleanup timer, an after-show listener
   * and a stored timer id — four moving parts, all of them working around not controlling the call site.
   *
   * The override is one-shot: it removes itself the moment it runs. A pending one is also cleared if the
   * pointer moves to a different activator before the panel ever opened.
   */
  let pendingInstant = null;

  const clearInstant = () => {
    if (pendingInstant === null) return;
    delete pendingInstant.createEnterAnimationControls;
    pendingInstant = null;
  };

  const suppressEnterAnimation = (panel) => {
    clearInstant();

    const original = Object.getPrototypeOf(panel)?.createEnterAnimationControls;
    if (typeof original !== 'function') return;

    pendingInstant = panel;

    panel.createEnterAnimationControls = function () {
      clearInstant();
      const controls = original.call(this);
      controls?.complete?.();
      return controls;
    };
  };

  /**
   * z-index is set explicitly because panels are siblings in DOM order: without it, moving from a later panel
   * to an earlier one paints the outgoing panel on top of the incoming one while it finishes hiding.
   */
  const closeOthers = (keep) => {
    document.querySelectorAll(OPEN_PANEL).forEach((panel) => {
      if (panel === keep) return;
      panel.style.zIndex = '';
      panel.dispatchEvent(new CustomEvent('dialog:force-close'));
    });
  };

  /**
   * pointerover is dispatched before pointerenter, so this runs before the theme's hover handler calls
   * show() — which is what makes it possible to install the animation override in time.
   *
   * The outgoing panel is closed from the incoming panel's dialog:before-show, which fires inside show()
   * immediately after [open] is set. That guarantees the incoming panel is already open before the outgoing
   * one closes, so a panel is open at every moment. It replaced a 30-frame requestAnimationFrame poll.
   */
  document.addEventListener('pointerover', (event) => {
    const activator = event.target.closest?.('[aria-controls][aria-expanded]');
    if (!activator) return;

    const target = document.getElementById(activator.getAttribute('aria-controls'));
    if (!target || target.tagName !== PANEL.toUpperCase() || target.hasAttribute('open')) return;

    if (anyOpen()) suppressEnterAnimation(target);

    target.style.zIndex = '2';

    if (!target.__ossahCloseHooked) {
      target.__ossahCloseHooked = true;
      target.addEventListener('dialog:before-show', () => {
        target.__ossahCloseHooked = false;
        closeOthers(target);
      }, { once: true });
    }
  }, { passive: true });

  /* ── 3. Hover-out ──────────────────────────────────────────────────────────────────────────────────── */

  /**
   * DialogElement closes a hover-opened panel from a pointerenter listener bound on document.body in the
   * CAPTURE phase. Its rule is: hide unless the entered element is the panel, a descendant of it, an ancestor
   * of it, or the activator. Anything else closes it — including the inline strips either side of the panel,
   * which belong to <main> rather than the header, and any page content the pointer crosses between the nav
   * row and the panel.
   *
   * Bound on document rather than body, and stopping propagation rather than immediate propagation. Capture
   * runs from the root down, so a document listener always precedes a body listener regardless of when
   * either was registered — where an earlier version relied on being registered first on the same node and
   * would have broken silently the day Snow moved that binding.
   *
   * Activators are exempt. Snow's open handler is bound on the same node for the same event, so suppressing
   * a target inside the header would also block the swap from one top-level item to the next.
   */
  document.addEventListener('pointerenter', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!anyOpen()) return;
    if (target.closest(ACTIVATOR)) return;
    if (target.closest(KEEP_OPEN)) event.stopPropagation();
  }, true);
})();